import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Base64 } from 'js-base64';
import { types as api } from '@mesa-labs/mesa-api';
import { VendorApi } from '@mesa-labs/mesa-api/dist/apis/vendor';

import { getIdToken, useApi } from './api';

export const serviceAgreementsApi = createApi({
  reducerPath: 'serviceAgreementsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.vendorUrl,
    prepareHeaders: async (headers) => {
      try {
        const idToken = await getIdToken();

        if (idToken) {
          headers.set('Authorization', `Bearer ${idToken}`);
        }
      } catch (err) {
        // no token available, don't add Authorization header
      }

      headers.set('mode', 'cors');
      headers.set('cache', 'no-cache');
      headers.set('credentials', 'same-origin');
      headers.set('Accept', 'application/json');
      headers.set('Content-Type', 'application/json');

      return headers;
    },
  }),
  tagTypes: ['ServiceAgreements', 'ActiveServiceAgreement'],
  endpoints: (build) => ({
    getAllServiceAgreements: build.query<readonly api.ServiceAgreementResponse[], { type: api.ServiceAgreementType }>({
      async queryFn({ type }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getAllServiceAgreements<api.ServiceAgreementResponse>(type);
        return { data };
      },
      providesTags: ['ServiceAgreements'],
    }),
    getActiveServiceAgreement: build.query<api.ServiceAgreementResponse | undefined, { type: api.ServiceAgreementType }>({
      async queryFn({ type }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getAllServiceAgreements<api.ServiceAgreementResponse>(type);
        return { data: data.find((sa) => sa.active) };
      },
      providesTags: ['ActiveServiceAgreement'],
    }),
    createServiceAgreement: build.mutation<api.ServiceAgreementResponse, { type: api.ServiceAgreementType } & api.ServiceAgreementRequest>({
      async queryFn({ htmlTemplate, type, ...rest }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createServiceAgreement<api.ServiceAgreementResponse>(type, {
          ...rest,
          htmlTemplate: Base64.encode(htmlTemplate),
        });
        return { data };
      },
      invalidatesTags: ['ServiceAgreements'],
    }),
    updateServiceAgreement: build.mutation<api.ServiceAgreementResponse, api.ServiceAgreementRequest & { type: api.ServiceAgreementType; version: number }>({
      async queryFn({ type, version, htmlTemplate, ...rest }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.updateServiceAgreement<api.ServiceAgreementResponse>(type, version, {
          ...rest,
          htmlTemplate: Base64.encode(htmlTemplate),
        });
        return { data };
      },
      invalidatesTags: ['ServiceAgreements'],
    }),
    activateServiceAgreement: build.mutation<api.ServiceAgreementResponse | undefined, { type: api.ServiceAgreementType, version: number }>({
      async queryFn({ type, version }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.activateServiceAgreement<api.ServiceAgreementResponse>(type, version);
        return { data };
      },
      invalidatesTags: ['ServiceAgreements', 'ActiveServiceAgreement'],
    }),
    renderLatestServiceAgreement: build.mutation<api.RenderedServiceAgreementResponse | undefined, { type: api.ServiceAgreementType } & api.RenderServiceAgreementRequest>({
      async queryFn({ type, ...request }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.renderLatestServiceAgreement(type, request);
        return { data };
      },
    }),
  }),
});

export const {
  useGetAllServiceAgreementsQuery,
  useGetActiveServiceAgreementQuery,
  useCreateServiceAgreementMutation,
  useUpdateServiceAgreementMutation,
  useActivateServiceAgreementMutation,
  useRenderLatestServiceAgreementMutation,
} = serviceAgreementsApi;
